import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface ClientFormData {
  name: string;
  type: 'corporate' | 'individual' | 'government' | 'other';
  director: string; 
  email?: string;
  phone?: string;
  address: string;
  website?: string;
  industry?: string;
  taxId?: string;
  referralSource?: string;
  notes?: string;
  discountPercentage?: number;
  discountReason?: string;
  status: 'active' | 'inactive' | 'potential';
}

const CLIENT_SOURCES = [
  'Рекомендация',
  'Интернет',
  'Реклама',
  'Холодный звонок',
  'Выставка',
  'Другое'
];

const CLIENT_TYPES = {
  corporate: 'Юридическое лицо',
  individual: 'Физическое лицо',
  government: 'Государственная организация',
  other: 'Другое'
};

const ClientForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialDiscount, setInitialDiscount] = useState<number | undefined>(undefined);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isDirty, dirtyFields }
  } = useForm<ClientFormData>({
    defaultValues: {
      name: '',
      type: 'corporate',
      director: '',
      phone: '',
      email: '',
      address: '',
      website: '',
      industry: '',
      taxId: '',
      referralSource: '',
      notes: '',
      discountPercentage: 0,
      discountReason: '',
      status: 'active'
    }
  });

  const watchedDiscount = watch('discountPercentage');

  useEffect(() => {
    // If in edit mode, fetch existing client data
    if (isEditMode) {
      setLoading(true);
      axios.get(`/clients/${id}`)
        .then(response => {
          const client = response.data;
          const currentDiscount = client.Discounts && client.Discounts.length > 0
            ? parseFloat(client.Discounts[0].discountPercentage)
            : 0;

          // Set form values
          setValue('name', client.name);
          setValue('type', client.type || 'corporate');
          setValue('director', client.director);
          setValue('phone', client.phone || '');
          setValue('email', client.email || '');
          setValue('address', client.address || '');
          setValue('website', client.website || '');
          setValue('industry', client.industry || '');
          setValue('taxId', client.taxId || '');
          setValue('referralSource', client.referralSource || '');
          setValue('notes', client.notes || '');
          setValue('discountPercentage', currentDiscount);
          setValue('status', client.status || 'active');

          // Store initial discount to check if it changed
          setInitialDiscount(currentDiscount);
        })
        .catch(err => {
          console.error('Error fetching client:', err);
          setError('Ошибка при загрузке данных клиента');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditMode, setValue]);

  const onSubmit = async (data: ClientFormData) => {
    setSubmitting(true);
    setError(null);

    // Prepare payload, only include reason if discount changed in edit mode
    const payload: Partial<ClientFormData> = { ...data };
    const discountChanged = isEditMode && dirtyFields.discountPercentage && data.discountPercentage !== initialDiscount;

    if (isEditMode) {
        if (!discountChanged) {
            delete payload.discountReason; // Don't send reason if discount didn't change
        } else if (!payload.discountReason) {
             // If discount changed but no reason provided, show error (or set default)
             setError('Пожалуйста, укажите причину изменения скидки.');
             setSubmitting(false);
             return;
        }
    } else {
        // Remove reason field completely when creating
        delete payload.discountReason;
    }

    // If creating and discount is 0 or undefined, don't send it
    if (!isEditMode && (!payload.discountPercentage || payload.discountPercentage === 0)) {
        delete payload.discountPercentage;
    }

    try {
      if (isEditMode) {
        await axios.put(`/clients/${id}`, payload);
      } else {
        await axios.post('/clients', payload);
      }

      // Navigate back to clients list on success
      navigate('/clients');
    } catch (err: any) {
      console.error('Error saving client:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении данных клиента');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Редактировать клиента' : 'Добавить клиента'}
        backLink="/clients"
      />

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Name */}
            <div className="md:col-span-1">
              <label htmlFor="name" className="form-label">
                Название компании / ФИО *
              </label>
              <input
                id="name"
                type="text"
                className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Название или ФИО"
                {...register('name', { required: 'Название / ФИО обязательно' })}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Type */}
            <div className="md:col-span-1">
              <label htmlFor="type" className="form-label">
                Тип клиента *
              </label>
              <select
                id="type"
                className={`form-input ${errors.type ? 'border-red-500' : ''}`}
                {...register('type', { required: 'Тип клиента обязателен' })}
              >
                {Object.entries(CLIENT_TYPES).map(([key, label]) => (
                   <option key={key} value={key}>{label}</option>
                ))}
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
              )}
            </div>

            {/* Status */}
            <div className="md:col-span-1">
              <label htmlFor="status" className="form-label">
                Статус клиента *
              </label>
              <select
                id="status"
                className={`form-input ${errors.status ? 'border-red-500' : ''}`}
                {...register('status', { required: 'Статус обязателен' })}
              >
                <option value="active">Активный</option>
                <option value="inactive">Неактивный</option>
                <option value="potential">Потенциальный</option>
              </select>
              {errors.status && (
                <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
              )}
            </div>

            {/* Director */}
            <div className="md:col-span-1">
              <label htmlFor="director" className="form-label">
                Директор
              </label>
              <input
                id="director"
                type="text"
                className={`form-input ${errors.director ? 'border-red-500' : ''}`}
                placeholder="ФИО директора"
                {...register('director', {  })}
              />
              {errors.director && (
                <p className="mt-1 text-sm text-red-600">{errors.director.message}</p>
              )}
            </div>
            
            {/* Phone */}
            <div>
              <label htmlFor="phone" className="form-label">
                Телефон
              </label>
              <input
                id="phone"
                type="text"
                className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="+7 (XXX) XXX-XX-XX"
                {...register('phone', { 
                  pattern: {
                    value: /^[+]?[0-9\s()-]{10,17}$/,
                    message: 'Введите корректный номер телефона'
                  }
                })}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="md:col-span-1">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                placeholder="email@example.com"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Введите корректный email'
                  }
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Address */}
            <div className="md:col-span-3">
              <label htmlFor="address" className="form-label">
                Адрес (Юридический/Фактический)
              </label>
              <input
                id="address"
                type="text"
                className="form-input"
                placeholder="Юридический адрес"
                {...register('address')}
              />
            </div>

            {/* Website */}
            <div className="md:col-span-1">
              <label htmlFor="website" className="form-label">Веб-сайт</label>
              <input id="website" type="url" className="form-input" placeholder="https://example.com" {...register('website')} />
            </div>

            {/* Industry */}
            <div className="md:col-span-1">
              <label htmlFor="industry" className="form-label">Отрасль</label>
              <input id="industry" type="text" className="form-input" placeholder="Например, Ритейл" {...register('industry')} />
            </div>

            {/* Tax ID */}
            <div className="md:col-span-1">
              <label htmlFor="taxId" className="form-label">ИНН/БИН</label>
              <input id="taxId" type="text" className="form-input" placeholder="12 цифр" {...register('taxId')} />
            </div>

            {/* Referral Source */}
            <div className="md:col-span-1">
              <label htmlFor="referralSource" className="form-label">Источник</label>
              <select id="referralSource" className="form-input" {...register('referralSource')}>
                <option value="">Выберите источник</option>
                {CLIENT_SOURCES.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            {/* Discount Percentage */}
            <div className="md:col-span-1">
              <label htmlFor="discountPercentage" className="form-label">
                Скидка (%)
              </label>
              <input
                id="discountPercentage"
                type="number"
                min="0"
                max="100"
                className={`form-input ${errors.discountPercentage ? 'border-red-500' : ''}`}
                {...register('discountPercentage', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Скидка не может быть отрицательной' },
                  max: { value: 100, message: 'Скидка не может превышать 100%' }
                })}
              />
              {errors.discountPercentage && (
                <p className="mt-1 text-sm text-red-600">{errors.discountPercentage.message}</p>
              )}
            </div>

            {/* Discount Reason */}
            {isEditMode && dirtyFields.discountPercentage && watchedDiscount !== initialDiscount && (
                <div className="md:col-span-1">
                    <label htmlFor="discountReason" className="form-label">Причина изменения скидки *</label>
                    <input
                        id="discountReason"
                        type="text"
                        className={`form-input ${errors.discountReason ? 'border-red-500' : ''}`}
                        placeholder="Например, Лояльность клиента"
                        {...register('discountReason', { required: 'Причина изменения скидки обязательна'})}
                    />
                    {errors.discountReason && (
                        <p className="mt-1 text-sm text-red-600">{errors.discountReason.message}</p>
                    )}
                </div>
            )}

            {/* Notes */}
            <div className="md:col-span-3">
              <label htmlFor="notes" className="form-label">Комментарии</label>
              <textarea
                id="notes"
                rows={4}
                className="form-input"
                placeholder="Любые дополнительные заметки о клиенте"
                {...register('notes')}
              />
            </div>
          </div>

          <div className="pt-5">
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => navigate('/clients')} className="btn-secondary" disabled={submitting}>
                Отмена
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || !isDirty}>
                {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить изменения' : 'Добавить клиента')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
